// Add someone / edit their details: name, email, department, job role, team lead / product manager ticks, start date and allowance,
// with the pro rata question for someone who starts part-way through a leave year.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { formatDays } from "../lib/dates";
import { aboveInTeam, teamHeads } from "../lib/orgchart";
import { proRata } from "../lib/proRata";
import { StoreError } from "../store/errors";
import { useStore } from "../store/StoreProvider";
import { useCtx } from "../store/useCtx";
import type { Role, User } from "../types";
import { byName } from "../ui/bits";
import { useDialogs } from "../ui/dialogs";
import { Icon } from "../ui/Icon";
import { Modal } from "../ui/Modal";
import { PhotoEditor } from "../ui/PhotoEditor";
import { useToast } from "../ui/Toasts";

const GENERIC_ERROR = "That did not work. You may not have permission, or the connection dropped.";

/** A number box's text as a number: null for blank, NaN for something that isn't a number. */
const asNumber = (text: string): number | null => (text.trim() === "" ? null : Number(text));

export function PersonDialog({ person, onClose }: { person?: User; onClose: () => void }) {
  const ctx = useCtx();
  const store = useStore();
  const toast = useToast();
  const { confirm } = useDialogs();
  const { settings } = ctx;
  const editing = !!person;

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [departmentId, setDepartmentId] = useState(person?.departmentId ?? "");
  // Typed, not picked: an existing job role is reused, a new one is created (after confirming) when you save.
  const [jobRoleText, setJobRoleText] = useState(ctx.jobRoles.find((j) => j.id === person?.jobRoleId)?.name ?? "");
  const [isLead, setIsLead] = useState(person?.isTeamLead ?? false);
  const [isPM, setIsPM] = useState(person?.isProductManager ?? false);
  const [reportsToId, setReportsToId] = useState(person?.reportsToId ?? "");
  const [role, setRole] = useState<Role>(person?.role ?? "employee");
  const [startDate, setStartDate] = useState(person ? (person.startDate ?? "") : ctx.today);
  const [allowanceText, setAllowanceText] = useState(
    typeof person?.allowance === "number" ? String(person.allowance) : "",
  );
  const [proRataOn, setProRataOn] = useState(person ? person.firstYearAllowance != null : true);
  const [proText, setProText] = useState(person?.firstYearAllowance != null ? String(person.firstYearAllowance) : "");
  const [proEdited, setProEdited] = useState(person?.firstYearAllowance != null);
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const firstInput = useRef<HTMLInputElement>(null);
  // After the dialog has opened: focusing before that does nothing, because a closed <dialog> can't take focus.
  useEffect(() => firstInput.current?.focus(), []);

  const typedJobRole = jobRoleText.trim();
  const existingJobRole = ctx.jobRoles.find((j) => j.name.toLowerCase() === typedJobRole.toLowerCase());
  const newJobRole = typedJobRole !== "" && !existingJobRole;

  const annualTyped = asNumber(allowanceText);
  const annual = annualTyped ?? settings.defaultAllowance;
  const annualValid = Number.isFinite(annual) && annual >= 0 && annual <= 366;
  const pr = startDate && annualValid ? proRata(annual, startDate, settings.leaveYearStartMonth) : null;
  const showProRata = !!pr?.partWay;
  // Until they type their own figure, the box follows the suggestion as the dates and allowance change.
  const proValue = proEdited ? proText : pr ? String(pr.days) : "";

  // Who is above this person because of their team and ticks, worked out for the person as they are now in the form.
  const isHR = role === "hr";
  const team = ctx.deptById.get(departmentId);
  const asEdited = {
    id: person?.id ?? "(new)",
    role,
    active: true,
    departmentId: departmentId || null,
    isTeamLead: !isHR && isLead,
    isProductManager: !isHR && isPM,
    reportsToId: reportsToId || null,
  };
  const others = ctx.users.filter((u) => u.id !== person?.id);
  const heads = teamHeads([...others, asEdited]);
  const aboveId = aboveInTeam(asEdited, heads);
  const above = aboveId ? ctx.userById.get(aboveId) : undefined;
  const aboveBecause = above && (above.isProductManager ? "product manager" : "team lead");
  /** Someone else (active, not HR) who already has this tick in the chosen team. */
  const holder = (tick: "isTeamLead" | "isProductManager") =>
    departmentId ? others.find((u) => u.departmentId === departmentId && u[tick]) : undefined;
  const leadHolder = holder("isTeamLead");
  const pmHolder = holder("isProductManager");
  const reportsToChoices = others.filter((u) => u.active && u.role !== "hr").sort(byName);

  const fail = (err: unknown) => {
    console.error(err);
    toast(err instanceof StoreError ? err.message : GENERIC_ERROR, "error");
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || photoBusy) return;
    if (!annualValid) return toast("Allowance must be between 0 and 366 days.", "error");
    const firstYear = showProRata && proRataOn ? asNumber(proValue) : null;
    if (firstYear !== null && !(Number.isFinite(firstYear) && firstYear >= 0 && firstYear <= 366)) {
      return toast("The allowance for their first year must be between 0 and 366 days.", "error");
    }
    // A blank box, or the company default typed out, means "follow the company default".
    const allowance = annualTyped === null || annualTyped === settings.defaultAllowance ? null : annualTyped;

    setBusy(true);
    try {
      // The job role first: reuse the one that exists (ignoring capitals), or create a new one once HR has confirmed.
      let jobRoleId: string | null = existingJobRole?.id ?? null;
      if (newJobRole) {
        const ok = await confirm({
          title: `Create the job role "${typedJobRole}"?`,
          message:
            "There is no job role with that name yet. It will be added to the list, so you can give it to other people too.",
          confirmLabel: "Create job role",
          cancelLabel: "Go back",
        });
        if (!ok) {
          setBusy(false);
          return;
        }
        await store.saveJobRole({ name: typedJobRole });
        const created = store.getState().jobRoles.find((j) => j.name.toLowerCase() === typedJobRole.toLowerCase());
        if (!created) throw new StoreError("The job role was created but could not be found. Please try again.");
        jobRoleId = created.id;
      }
      if (existingJobRole || jobRoleId) setJobRoleText(existingJobRole?.name ?? typedJobRole);

      const details = {
        departmentId: departmentId || null,
        jobRoleId,
        isTeamLead: isLead,
        isProductManager: isPM,
        reportsToId: reportsToId || null,
        startDate: startDate || null,
        allowance,
        firstYearAllowance: firstYear,
      };
      if (person) {
        const patch = Object.fromEntries(
          Object.entries({ ...details, role }).filter(
            ([key, value]) => (person as unknown as Record<string, unknown>)[key] !== value,
          ),
        );
        if (Object.keys(patch).length) await store.saveUser(person.id, patch);
        toast(`${person.name} updated.`);
      } else {
        const name = `${first.trim()} ${last.trim()}`.trim();
        await store.addUser({ name, email: email.trim(), role, ...details });
        toast(`${name} added. They can now sign in with ${email.trim().toLowerCase()}.`);
      }
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  return (
    <Modal title={person ? person.name : "Add someone"} titleId="person-title" wide onClose={() => { if (!photoBusy) onClose(); }}>
      {person && <PhotoEditor userId={person.id} onBusyChange={setPhotoBusy} />}
      <form onSubmit={save} className="person-form">
        {person ? (
          <p className="muted person-email">{person.email}</p>
        ) : (
          <>
            <p className="hint">
              Only people on this list can sign in. Use the email address they sign in to Microsoft with.
            </p>
            <div className="grid-2">
              <div className="field">
                <label htmlFor="np-first">First name</label>
                <input
                  id="np-first"
                  type="text"
                  maxLength={120}
                  required
                  ref={firstInput}
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="np-last">Last name</label>
                <input
                  id="np-last"
                  type="text"
                  maxLength={120}
                  required
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="np-email">Email address</label>
              <input
                id="np-email"
                type="email"
                required
                placeholder="name@yourcompany.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="grid-2">
          <div className="field">
            <label htmlFor="np-dept">Department</label>
            <select id="np-dept" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">No department</option>
              {ctx.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="np-job">Job role</label>
            <input
              id="np-job"
              type="text"
              list="np-job-options"
              autoComplete="off"
              maxLength={60}
              placeholder="e.g. Developer"
              value={jobRoleText}
              onChange={(e) => setJobRoleText(e.target.value)}
            />
            <datalist id="np-job-options">
              {ctx.jobRoles.map((j) => (
                <option key={j.id} value={j.name} />
              ))}
            </datalist>
            <p className="hint">
              {newJobRole
                ? `"${typedJobRole}" is a new job role. You'll be asked to confirm before it is created.`
                : "Start typing to pick an existing job role, or type a new one. Leave blank for none."}
            </p>
          </div>
        </div>

        <fieldset className="field team-role">
          <legend>Role in the team</legend>
          <div className="check-row">
            <label>
              <input
                type="checkbox"
                checked={!isHR && isLead}
                disabled={isHR || !departmentId || !!leadHolder}
                onChange={(e) => {
                  setIsLead(e.target.checked);
                  if (e.target.checked) setIsPM(false);
                }}
              />{" "}
              Team lead
            </label>
            <label>
              <input
                type="checkbox"
                checked={!isHR && isPM}
                disabled={isHR || !departmentId || !!pmHolder}
                onChange={(e) => {
                  setIsPM(e.target.checked);
                  if (e.target.checked) setIsLead(false);
                }}
              />{" "}
              Product manager
            </label>
          </div>
          <p className="hint">
            {isHR
              ? "HR approve everyone's leave, so they are not part of a team's chart."
              : !departmentId
                ? "Choose a department first: the team lead and product manager belong to a team."
                : [
                    leadHolder && `${team?.name} already has a team lead: ${leadHolder.name}. Untick them first.`,
                    pmHolder && `${team?.name} already has a product manager: ${pmHolder.name}. Untick them first.`,
                  ]
                    .filter(Boolean)
                    .join(" ") ||
                  "Everyone else in the team reports to the team lead, who reports to the product manager. They can approve the leave of the people who report to them."}
          </p>
        </fieldset>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="np-role">Access</label>
            <select id="np-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="employee">Employee</option>
              <option value="hr">HR</option>
            </select>
            <p className="hint">HR can approve everyone's leave and use the Admin area.</p>
          </div>
          <div className="field">
            {isHR ? (
              <>
                <span className="label">Reports to</span>
                <p className="static">Not needed for HR</p>
              </>
            ) : above ? (
              <>
                <span className="label">Reports to</span>
                <p className="static">
                  {above.name} ({aboveBecause} of {team?.name})
                </p>
                <p className="hint">
                  Worked out from the team. It changes by itself when the team lead or product manager does.
                </p>
              </>
            ) : (
              <>
                <label htmlFor="np-reports">Reports to</label>
                <select id="np-reports" value={reportsToId} onChange={(e) => setReportsToId(e.target.value)}>
                  <option value="">HR</option>
                  {reportsToChoices.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
                <p className="hint">
                  Nobody in this team is above them, so they report to HR unless you choose someone, such as a Head of Product.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="np-start">Employment start date</label>
            <input id="np-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="np-allowance">Annual allowance (days)</label>
            <input
              id="np-allowance"
              type="number"
              min={0}
              max={366}
              step={0.5}
              inputMode="decimal"
              placeholder={formatDays(settings.defaultAllowance)}
              value={allowanceText}
              onChange={(e) => setAllowanceText(e.target.value)}
            />
            <p className="hint">Leave blank for the company default of {formatDays(settings.defaultAllowance)} days.</p>
          </div>
        </div>

        {showProRata && pr && (
          <fieldset className="field pro-rata">
            <legend>Pro rata allowance?</legend>
            <p className="hint">
              {person ? "This person" : "They are"} starting part way through a leave year. You might want to pro rata
              their allowance.
            </p>
            <div className="radio-row">
              <label>
                <input type="radio" name="pro-rata" checked={proRataOn} onChange={() => setProRataOn(true)} /> Yes
              </label>
              <label>
                <input type="radio" name="pro-rata" checked={!proRataOn} onChange={() => setProRataOn(false)} /> No
              </label>
            </div>
            {proRataOn ? (
              <>
                <p className="pro-rata-line">
                  <span>Set their {pr.yearLabel} allowance to</span>
                  <input
                    className="num"
                    type="number"
                    aria-label={`${pr.yearLabel} allowance in days`}
                    min={0}
                    max={366}
                    step={0.5}
                    inputMode="decimal"
                    value={proValue}
                    onChange={(e) => {
                      setProText(e.target.value);
                      setProEdited(true);
                    }}
                  />
                  <span>days</span>
                </p>
                <p className="hint">
                  Suggested: {formatDays(annual)} days × {pr.daysLeft} of {pr.daysInYear} days left in the leave year,
                  rounded to the nearest half day = {formatDays(pr.days)}. You can type a different number.
                </p>
              </>
            ) : (
              <p className="hint">
                They get the full {formatDays(annual)} days for {pr.yearLabel}.
              </p>
            )}
          </fieldset>
        )}

        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" disabled={photoBusy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn" disabled={busy || photoBusy}>
            <Icon name="check" size={18} />
            {editing ? "Save changes" : "Save user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
